import { useEffect, useRef, useState } from 'react';
import type { PhotoWallSettings } from '@karozik/engine';
import type { ControlCommand, PublicQueueItem, PublicState } from '@karozik/shared';
import { Artwork, Avatar, cn, Equalizer, MomentControls, Spinner, TrackLine, useParty, useTrackSearch } from '@karozik/ui';
import { useLyrics } from '../screen/useLyrics';
import type { AdminEvent } from '../types';

/** The organiser's remote, designed for a phone held in one hand. */
export function LiveTab({ event }: { event: AdminEvent }) {
  const conn = useParty({ role: 'admin', eventId: event.id }, { keepReactions: false });
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const state = conn.state;

  const send = async (c: ControlCommand) => {
    const r = await conn.send('control', c);
    setError(r.ok ? null : (r.error ?? 'Erreur'));
  };

  if (!state) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  const np = state.nowPlaying;

  return (
    <div className="space-y-6">
      {!state.staff?.playerOnline && (
        <div className="rounded-2xl border border-sun/40 bg-sun/10 px-4 py-3 text-sm text-sun">
          ⚠️ Aucun lecteur connecté : ouvre <b>▶ Lecteur</b> sur le Mac relié aux enceintes.
        </div>
      )}
      {error && <p className="rounded-xl bg-danger/15 px-4 py-3 text-sm text-danger">{error}</p>}

      <section className="rounded-3xl bg-surface p-4">
        <div className="flex items-center gap-4">
          <Artwork track={np?.track ?? null} className="h-24 w-24" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs font-bold tracking-widest text-pink uppercase">
              <Equalizer playing={!state.paused && !!np} /> {state.paused ? 'Pause' : 'En cours'} · {state.phase.emoji} {state.phase.name}
            </div>
            <div className="mt-1 truncate text-lg font-bold">{np?.track.title ?? '—'}</div>
            <div className="truncate text-ink-dim">{np?.track.artist}</div>
            <div className="mt-1 text-xs text-ink-faint">
              👥 {state.guestsOnline} connectés · 🔥 {state.heat}%{np && np.skipVoters.length > 0 ? ` · 👎 ${np.skipVoters.length}/${np.skipNeeded}` : ''}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={() => void send({ type: state.paused ? 'play' : 'pause' })} className="rounded-2xl bg-surface-2 py-3 text-lg font-semibold">
            {state.paused ? '▶ Lecture' : '⏸ Pause'}
          </button>
          <button onClick={() => void send({ type: 'skip' })} className="rounded-2xl bg-surface-2 py-3 text-lg font-semibold">
            ⏭ Passer
          </button>
          <button
            onClick={() => np && confirm(`Bannir « ${np.track.title} » pour la soirée ?`) && void send({ type: 'banTrack', trackId: np.track.id })}
            className="rounded-2xl bg-surface-2 py-3 text-lg font-semibold"
          >
            🚫 Bannir
          </button>
        </div>
        <label className="mt-4 flex items-center gap-3">
          <span className="text-xl">🔈</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={state.volume}
            onChange={(e) => void send({ type: 'volume', value: Number(e.target.value) })}
            className="flex-1 accent-pink"
          />
          <span className="w-10 text-right text-sm text-ink-dim">{Math.round(state.volume * 100)}%</span>
        </label>
        <LyricsToggle eventId={event.id} state={state} send={send} />
      </section>

      <PhotoWallControls state={state} send={send} />

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">File d’attente</h2>
          <button onClick={() => setAdding(true)} className="rounded-xl bg-surface-2 px-3 py-1.5 text-sm font-semibold">
            ＋ Ajouter
          </button>
        </div>
        <ol className="space-y-2">
          {state.queue.map((item) => (
            <QueueAdminRow key={item.id} item={item} send={send} />
          ))}
        </ol>
      </section>

      <section className="rounded-3xl bg-surface p-4">
        <MomentControls conn={conn} showSkip={false} />
      </section>

      {adding && <AddTrack onClose={() => setAdding(false)} onAdd={(trackId, pin) => send({ type: 'add', trackId, pin })} />}
    </div>
  );
}

/** Lyrics on the big screen: one tap, plus what was found for the current track. */
function LyricsToggle({ eventId, state, send }: { eventId: string; state: PublicState; send(c: ControlCommand): Promise<void> }) {
  const on = !!state.staff?.showLyrics;
  const lyrics = useLyrics(eventId, state.nowPlaying?.track.id ?? null, on);
  const status = !on
    ? null
    : !state.nowPlaying
      ? null
      : lyrics === undefined
        ? 'Recherche des paroles…'
        : lyrics?.synced
          ? '✓ Paroles synchronisées trouvées'
          : lyrics?.instrumental
            ? 'Instrumental'
            : lyrics?.plainOnly
              ? 'Seulement le texte, sans le minutage : rien à l’écran'
              : lyrics === null
                ? 'LRCLIB ne répond pas, nouvel essai dans 20 s…'
                : 'Paroles introuvables pour ce titre';
  return (
    <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-surface-2 px-4 py-3">
      <div className="min-w-0">
        <div className="font-semibold">🎤 Paroles à l’écran</div>
        {status && <div className="truncate text-xs text-ink-faint">{status}</div>}
        {on && state.provider === 'preview' && <div className="text-xs text-sun">Extraits de 30 s : paroles décalées</div>}
      </div>
      <button
        onClick={() => void send({ type: 'lyrics', show: !on })}
        className={cn('shrink-0 rounded-full px-4 py-2 text-sm font-bold', on ? 'bg-gradient-neon' : 'bg-surface')}
        aria-pressed={on}
      >
        {on ? 'Activées' : 'Désactivées'}
      </button>
    </div>
  );
}

/** Gaps offered in interlude mode, from a photo every few songs' worth to one per song. */
const GAPS = [10, 15, 20, 30, 45, 60, 90, 120, 180, 300, 600];

function formatSeconds(s: number): string {
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return s % 60 ? `${m} min ${s % 60}` : `${m} min`;
}

/** The guests' photos on the big screen: on/off and tuning, applied live. */
function PhotoWallControls({ state, send }: { state: PublicState; send(c: ControlCommand): Promise<void> }) {
  const wall = state.staff?.photoWall;
  if (!wall) return null;
  const set = (p: Partial<PhotoWallSettings>) => void send({ type: 'photoWall', ...p });
  const paused = wall.enabled && (!!state.blindtest || !!state.faces || !!state.moment || state.mic.active);
  const status =
    state.photoCount === 0
      ? 'Aucune photo partagée pour l’instant : l’écran reste normal.'
      : `${state.photoCount} photo${state.photoCount > 1 ? 's' : ''} partagée${state.photoCount > 1 ? 's' : ''}${paused ? ' · en pause pendant les jeux, moments et prises de parole' : ''}`;
  const gapIndex = GAPS.reduce((best, g, i) => (Math.abs(g - wall.gapSeconds) < Math.abs(GAPS[best]! - wall.gapSeconds) ? i : best), 0);

  return (
    <section className="space-y-4 rounded-3xl bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold">📷 Photos à l’écran</div>
          <div className="text-xs text-ink-faint">{status}</div>
        </div>
        <button
          onClick={() => set({ enabled: !wall.enabled })}
          className={cn('shrink-0 rounded-full px-4 py-2 text-sm font-bold', wall.enabled ? 'bg-gradient-neon' : 'bg-surface-2')}
          aria-pressed={wall.enabled}
        >
          {wall.enabled ? 'Activées' : 'Désactivées'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {(
          [
            ['interlude', 'En alternance', 'L’écran habituel, et une photo de temps en temps.'],
            ['continuous', 'En continu', 'Les photos s’enchaînent, le titre en cours en bas.'],
          ] as const
        ).map(([mode, label, hint]) => (
          <button
            key={mode}
            onClick={() => set({ mode })}
            aria-pressed={wall.mode === mode}
            className={cn('rounded-2xl p-3 text-left', wall.mode === mode ? 'bg-surface-3 ring-2 ring-pink' : 'bg-surface-2')}
          >
            <div className="font-semibold">{label}</div>
            <div className="text-xs text-ink-dim">{hint}</div>
          </button>
        ))}
      </div>
      <LiveSlider label="Durée de chaque photo" value={wall.seconds} min={3} max={30} display={(v) => `${v} s`} onCommit={(seconds) => set({ seconds })} />
      {wall.mode === 'interlude' && (
        <LiveSlider
          label="Écran habituel entre deux photos"
          value={gapIndex}
          min={0}
          max={GAPS.length - 1}
          display={(i) => formatSeconds(GAPS[i]!)}
          onCommit={(i) => set({ gapSeconds: GAPS[i]! })}
        />
      )}
      <label className="flex items-center justify-between gap-4">
        <span>
          Nouvelles photos en priorité
          <span className="block text-xs text-ink-faint">Une photo qui vient d’être partagée passe tout de suite, avec « Nouvelle photo ! ».</span>
        </span>
        <input type="checkbox" checked={wall.newFirst} onChange={(e) => set({ newFirst: e.target.checked })} className="h-6 w-6 shrink-0 accent-pink" />
      </label>
      <label className="flex items-center justify-between gap-4">
        <span>Afficher qui a pris la photo</span>
        <input type="checkbox" checked={wall.showAuthor} onChange={(e) => set({ showAuthor: e.target.checked })} className="h-6 w-6 shrink-0 accent-pink" />
      </label>
      {wall.mode === 'continuous' && state.staff?.showLyrics && <p className="text-xs text-sun">Les paroles sont cachées derrière les photos en mode continu.</p>}
    </section>
  );
}

/** A slider whose value goes to the server once the finger stops moving, not at every step. */
function LiveSlider(props: { label: string; value: number; min: number; max: number; display(v: number): string; onCommit(v: number): void }) {
  const { value, onCommit } = props;
  const [draft, setDraft] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-3">
        <span>{props.label}</span>
        <span className="text-sm text-ink-dim tabular-nums">{props.display(draft)}</span>
      </span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={1}
        value={draft}
        onChange={(e) => {
          const v = Number(e.target.value);
          setDraft(v);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => onCommit(v), 400);
        }}
        className="mt-2 w-full accent-pink"
      />
    </label>
  );
}

function QueueAdminRow({ item, send }: { item: PublicQueueItem; send(c: ControlCommand): Promise<void> }) {
  return (
    <li className={cn('rounded-2xl bg-surface p-2.5', item.pinned && 'ring-1 ring-pink/60')}>
      <TrackLine
        track={item.track}
        sub={
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-ink-faint">
            {item.proposer ? (
              <>
                <Avatar guest={item.proposer} size="sm" /> {item.proposer.pseudo}
              </>
            ) : (
              { autopilot: '🤖 Autopilote', admin: '🎛️ Régie', moment: '✨ Moment', guest: '' }[item.source]
            )}
            {item.votes > 0 && <span>· ❤️ {item.votes}</span>}
            {item.dedication && <span className="truncate">· 💌 {item.dedication}</span>}
          </div>
        }
        right={
          <div className="flex shrink-0 gap-1">
            <button
              title={item.pinned ? 'Désépingler' : 'Jouer ensuite'}
              onClick={() => void send({ type: item.pinned ? 'unpin' : 'pin', itemId: item.id })}
              className={cn('rounded-lg px-2 py-1.5', item.pinned ? 'bg-pink' : 'bg-surface-2')}
            >
              📌
            </button>
            <button title="Retirer" onClick={() => void send({ type: 'remove', itemId: item.id })} className="rounded-lg bg-surface-2 px-2 py-1.5">
              ✕
            </button>
          </div>
        }
      />
    </li>
  );
}

function AddTrack({ onClose, onAdd }: { onClose(): void; onAdd(trackId: string, pin: boolean): Promise<void> }) {
  const [q, setQ] = useState('');
  const { result, loading } = useTrackSearch(q, {});
  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-night/95 px-4 pt-4 backdrop-blur">
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col overflow-hidden">
        <div className="flex gap-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre, artiste…" className="input flex-1 text-lg" />
          <button onClick={onClose} className="rounded-xl bg-surface-2 px-4">
            Fermer
          </button>
        </div>
        <div className="mt-3 flex-1 overflow-y-auto pb-8">
          {loading && <Spinner />}
          <ul className="space-y-2">
            {result?.tracks.map((t) => (
              <li key={t.id} className="rounded-2xl bg-surface p-2.5">
                <TrackLine
                  track={t}
                  right={
                    <div className="flex shrink-0 gap-1">
                      <button onClick={() => void onAdd(t.id, true).then(onClose)} className="bg-gradient-neon rounded-lg px-2.5 py-1.5 text-sm font-bold">
                        Ensuite
                      </button>
                      <button onClick={() => void onAdd(t.id, false).then(onClose)} className="rounded-lg bg-surface-2 px-2.5 py-1.5 text-sm">
                        File
                      </button>
                    </div>
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
